import React, { useState, useEffect, useRef } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, InputAdornment, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BadgeIcon from '@mui/icons-material/Badge';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery, useMutation } from '@apollo/client';
import DateRangeFilter from '../../components/DateRangeFilter';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/useAuth';
import {
  GET_PD_CASES,
  GET_PD_CANDIDATES,
  GET_PD_CASE_SUMMARY,
  OPEN_PD_CASE,
  PAUSE_PD_CASE,
  RESUME_PD_CASE,
  CLOSE_PD_CASE,
  SUPPLY_PD_CASE_KYC,
  TRIGGER_PD_RSU,
  SKIP_PD_RSU,
  TRIGGER_PD_BPC,
  PdCase,
  PdCaseKycInput,
  PdCandidate,
  PdCaseStatus,
  PdCaseListData,
  PdCandidateListData,
  PdCaseSummaryData,
} from './queries';
import {
  fmtEpoch,
  fmtAgoEpoch,
  stepChipColor,
  progressOf,
  getRsuStatusMessage,
  isRsuReadyAt,
} from './format';

/**
 * PD revival board.
 *
 * Deliberately narrow. A revival carries four Sensorise service requests, three
 * srIds and an IMSI ticket token, and none of that helps the person whose job
 * on this page is to decide whether to press RSU — so the table shows who the
 * customer is, how far the case has got, how long it has been sat there, and
 * a contextual action. A pending RSU can also be explicitly skipped once OTC
 * RSU and IMSI have completed; that choice is confirmed before the BPC is sent.
 * The srIds live on the row's tooltip for when something has gone wrong and
 * someone has to ring Sensorise.
 *
 * The buttons are contextual rather than one column per step. Availability
 * comes from the server so an enabled action can never produce a refusal.
 */

type Tab = 'AWAITING_RSU' | 'AWAITING_BPC' | 'LIVE' | 'ALL' | 'CANDIDATES';

/**
 * How often the board re-reads itself.
 *
 * A revival moves without anyone touching this page: the sweep polls
 * Sensorise every 15 minutes and can take a case from OTC RSU through IMSI
 * procurement to AWAITING_RSU in one pass (case 8be6f178 did exactly that at
 * 18:00 on 2026-09-22). A static board shows none of it, so the operator
 * either keeps pressing F5 or works from a screen that is quietly an hour
 * old -- and on this page a stale row means pressing RSU on a case whose
 * state has already moved.
 *
 * 30s rather than matching the 15-minute sweep: manual RSU/BPC actions by
 * another operator land between sweeps too, and this is a small paginated
 * table.
 */
const POLL_MS = 30000;

/** Polling is suspended while the tab is hidden -- this board sits open on a
 *  spare monitor all day, and an unwatched tab has nobody to show it to. The
 *  visibility listener below refetches the moment it comes back, so returning
 *  to the tab never shows a stale screen. */
const pausedWhenHidden = () => document.visibilityState === 'hidden';

const TABS: Array<{ key: Tab; label: string; status?: string }> = [
  // The default and first. No status variable at all: the backend's default
  // view is "work in hand", i.e. everything except COMPLETED, CANCELLED and
  // CLOSED.
  // It used to open on Awaiting RSU, but a case that FAILED on its very first
  // step -- 52d313cd on 2026-09-22, rejected by Sensorise before the operator
  // had seen it -- was on no queue tab at all, and the one place it showed
  // was third. Everything open is what a person needs to see first; the two
  // step queues that follow are that same set narrowed to one button each.
  { key: 'LIVE', label: 'All open', status: undefined },
  { key: 'AWAITING_RSU', label: 'Awaiting RSU', status: 'AWAITING_RSU' },
  { key: 'AWAITING_BPC', label: 'Awaiting BPC', status: 'AWAITING_BPC' },
  { key: 'ALL', label: 'History', status: 'ALL' },
  // Not a status filter on pd_case at all: a different query over
  // sim_inventory, and the only tab from which a case can be started. Last,
  // because starting work is the exception -- the other four are the work.
  { key: 'CANDIDATES', label: 'Not started', status: undefined },
];

const CASE_COLUMNS = [
  'Sr.No', 'Customer', 'Phone', 'VIN', 'IMEI', 'ICCID', 'Progress', 'Waiting', 'Action',
];

// No Progress or Waiting column: nothing has been sent for a candidate, so both
// would be blank. KYC takes their place, because it is the one thing that
// changes the odds of the case succeeding.
const CANDIDATE_COLUMNS = [
  'Sr.No', 'Customer', 'Phone', 'VIN', 'IMEI', 'ICCID', 'KYC', 'Expired', 'Action',
];

/**
 * Document types, in the kyc_details vocabulary the backend stores and the
 * mkycEntity carries verbatim. Taken from what is already on file (PAN/Aadhar
 * dominate the 31k rows) rather than the recharge portal's lowercase
 * `aadhar`/`pan`: Sensorise accepted both spellings on the three completed
 * revivals, and matching the existing rows keeps the data consistent.
 */
const POI_TYPES = [
  { value: 'PAN', label: 'PAN Card' },
  { value: 'Aadhar', label: 'Aadhaar Card' },
  { value: 'Driving_Licence', label: 'Driving Licence' },
  { value: 'Voter_Id', label: 'Voter ID' },
  { value: 'Passport', label: 'Passport' },
];
const POA_TYPES = [
  { value: 'Aadhar', label: 'Aadhaar Card' },
  { value: 'GST Certificate', label: 'GST Certificate' },
  { value: 'Driving_Licence', label: 'Driving Licence' },
  { value: 'Voter_Id', label: 'Voter ID' },
  { value: 'Passport', label: 'Passport' },
  { value: 'Electricity', label: 'Electricity Bill' },
];

const EMPTY_KYC: Required<PdCaseKycInput> = {
  proofOfIdentity: '',
  poiIdNumber: '',
  proofOfAddress: '',
  poaIdNumber: '',
  cusName: '',
  cusContNo: '',
  cusAdrs: '',
};

/** The step parked on missing KYC, if any. Only step 1 ever is in practice. */
const kycBlockedStep = (row: PdCase) => row.steps.find((s) => s.status === 'BLOCKED');

const cellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: { xs: '4px', sm: '6px' },
  fontSize: { xs: '0.75rem', sm: '0.85rem' },
  borderRight: '1px solid #e0e0e0',
};

const subTabStyle = (isActive: boolean) => ({
  fontWeight: 600,
  fontSize: '1rem',
  px: 3,
  py: 1.5,
  borderRadius: '20px',
  textTransform: 'none' as const,
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  color: isActive ? '#fff' : '#555',
  backgroundColor: isActive ? '#E60012' : 'transparent',
  '&:hover': { backgroundColor: isActive ? '#E60012' : '#ffffffc0' },
  minWidth: 'auto',
});

const PdCases = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canUpdate, canCreate } = useAuth();

  const [tab, setTab] = useState<Tab>('LIVE');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Date bounds on opened_at. Kept separate from the tabs: the tabs ARE the
  // status filter here, so the shared FilterBar is mounted date-only below
  // rather than offering a second, competing status control.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [busyCase, setBusyCase] = useState<string | null>(null);
  // The candidate awaiting confirmation. Opening a case spends the OTC RSU fee
  // the moment it is confirmed, so it does not happen on a single click.
  const [confirming, setConfirming] = useState<PdCandidate | null>(null);
  const [skippingRsuFor, setSkippingRsuFor] = useState<PdCase | null>(null);
  const [opening, setOpening] = useState(false);
  // The case being paused, and the note the operator types for it. A pause
  // is cheap and reversible, so this is one field, not a warning.
  const [pausing, setPausing] = useState<PdCase | null>(null);
  const [pauseNote, setPauseNote] = useState('');
  // The case being closed, and why. Unlike a pause this is permanent, so the
  // reason is required and the dialog says there is no way back.
  const [closing, setClosing] = useState<PdCase | null>(null);
  const [closeReason, setCloseReason] = useState('');
  // The case whose blocked step 1 is being given its KYC, the form, and the
  // server's last answer. The answer stays on the dialog when the step still
  // could not go out, because it names what is STILL missing.
  const [kycFor, setKycFor] = useState<PdCase | null>(null);
  const [kycForm, setKycForm] = useState(EMPTY_KYC);
  const [kycAnswer, setKycAnswer] = useState<string | null>(null);
  const [savingKyc, setSavingKyc] = useState(false);
  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));
  // When the visible data was last read, for the "updated Ns ago" caption.
  const [lastLoadedAt, setLastLoadedAt] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const tick = () => setNowSeconds(Math.floor(Date.now() / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  /**
   * Coming back to the tab reads the board immediately.
   *
   * Polling is suspended while hidden (skipPollAttempt), so without this the
   * first thing an operator sees on returning is up to POLL_MS of stale rows
   * -- which on this page is the screen they decide whether to press RSU
   * from. refetchAll is deliberately not in the dep array: Apollo's refetch
   * identity is stable enough here, and re-subscribing on every render would
   * fire a request per render instead of per visibility change.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void refetchAllRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    setPage(0);
  }, [tab, debouncedSearch, fromDate, toDate]);

  const activeTab = TABS.find((t) => t.key === tab)!;
  const onCandidates = tab === 'CANDIDATES';

  const { data, loading, error, refetch } = useQuery<PdCaseListData>(GET_PD_CASES, {
    variables: {
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      status: activeTab.status,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    },
    fetchPolicy: 'cache-and-network',
    // The two tables are different queries over different tables. Skipping the
    // idle one keeps a tab switch to one request instead of two.
    skip: onCandidates,
    pollInterval: POLL_MS,
    skipPollAttempt: pausedWhenHidden,
  });

  const {
    data: candidateData,
    loading: candidatesLoading,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useQuery<PdCandidateListData>(GET_PD_CANDIDATES, {
    variables: {
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
    },
    fetchPolicy: 'cache-and-network',
    skip: !onCandidates,
    pollInterval: POLL_MS,
    skipPollAttempt: pausedWhenHidden,
  });

  const { data: summaryData, refetch: refetchSummary } =
    useQuery<PdCaseSummaryData>(GET_PD_CASE_SUMMARY, {
      fetchPolicy: 'cache-and-network',
      pollInterval: POLL_MS,
      skipPollAttempt: pausedWhenHidden,
    });

  /**
   * Re-read whichever table is on screen, plus the summary strip.
   *
   * Held in a ref so the visibility effect can call the CURRENT version
   * without re-subscribing whenever a query object changes identity.
   */
  const refetchAll = async (): Promise<void> => {
    await Promise.all([
      onCandidates ? refetchCandidates() : refetch(),
      refetchSummary(),
    ]);
  };
  const refetchAllRef = useRef(refetchAll);
  refetchAllRef.current = refetchAll;

  // Every completed read stamps the caption, including the polled ones.
  useEffect(() => {
    if (!loading && !candidatesLoading) {
      setLastLoadedAt(Math.floor(Date.now() / 1000));
    }
  }, [data, candidateData, loading, candidatesLoading]);

  const [triggerRsu] = useMutation(TRIGGER_PD_RSU);
  const [skipRsu] = useMutation(SKIP_PD_RSU);
  const [triggerBpc] = useMutation(TRIGGER_PD_BPC);
  const [openCase] = useMutation(OPEN_PD_CASE);
  const [pauseCase] = useMutation(PAUSE_PD_CASE);
  const [resumeCase] = useMutation(RESUME_PD_CASE);
  const [closeCase] = useMutation(CLOSE_PD_CASE);
  const [supplyKyc] = useMutation(SUPPLY_PD_CASE_KYC);

  const rows = data?.pdCases?.cases ?? [];
  const candidates = candidateData?.pdCandidates?.candidates ?? [];
  const totalCount = onCandidates
    ? candidateData?.pdCandidates?.total ?? 0
    : data?.pdCases?.total ?? 0;
  const summary = summaryData?.pdCaseSummary;

  const columns = onCandidates ? CANDIDATE_COLUMNS : CASE_COLUMNS;
  const listLoading = onCandidates ? candidatesLoading : loading;
  const listError = onCandidates ? candidatesError : error;
  const listLength = onCandidates ? candidates.length : rows.length;

  const mayTrigger = canUpdate('PD-CASE');
  // Deliberately create, not update: the server gates openPdCase on
  // create_access, and showing a button the server will refuse is worse than
  // not showing one.
  const mayOpen = canCreate('PD-CASE');

  /**
   * RSU and BPC transport actions go through here. Skipping RSU is separate
   * because it only changes the case state and must not send a plan.
   *
   * The mutation reports failure in its payload rather than throwing — "the
   * IMSI has not come back yet" is an answer the operator needs to read, not a
   * stack trace — so a `success: false` is surfaced as a warning with the
   * server's own words, not swallowed as a success.
   */
  const fire = async (
    row: PdCase,
    which: 'RSU' | 'BPC',
  ): Promise<void> => {
    setBusyCase(row.caseId);
    try {
      const run = which === 'RSU' ? triggerRsu : triggerBpc;
      const res = await run({ variables: { caseId: row.caseId } });
      const payload =
        which === 'RSU'
          ? res.data?.triggerPdRsu
          : res.data?.triggerPdBpc;

      enqueueSnackbar(payload?.message || `${which} sent`, {
        variant: payload?.success ? 'success' : 'warning',
      });
      await Promise.all([refetch(), refetchSummary()]);
    } catch (err: any) {
      enqueueSnackbar(err?.message || `Could not send the ${which}`, {
        variant: 'error',
      });
    } finally {
      setBusyCase(null);
    }
  };

  const confirmSkipRsu = async (): Promise<void> => {
    if (!skippingRsuFor) return;
    const row = skippingRsuFor;
    setBusyCase(row.caseId);
    try {
      const res = await skipRsu({ variables: { caseId: row.caseId } });
      const payload = res.data?.skipPdRsu;
      enqueueSnackbar(payload?.message || 'RSU skipped', {
        variant: payload?.success ? 'success' : 'warning',
      });
      if (payload?.success) setSkippingRsuFor(null);
      await Promise.all([refetch(), refetchSummary()]);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not skip RSU', {
        variant: 'error',
      });
    } finally {
      setBusyCase(null);
    }
  };

  /**
   * Start a revival. Only ever reached through the confirmation dialog.
   *
   * On success the operator is moved off the candidates tab and onto the live
   * queue, because the case they just created is now there and its OTC RSU has
   * already gone out -- leaving them staring at the candidate list, minus one
   * row, hides the thing that just happened.
   */
  const confirmOpen = async (): Promise<void> => {
    if (!confirming) return;
    setOpening(true);
    try {
      const res = await openCase({ variables: { iccid: confirming.iccid } });
      const payload = res.data?.openPdCase;
      enqueueSnackbar(payload?.message || 'PD case opened', {
        variant: payload?.success ? 'success' : 'warning',
      });
      setConfirming(null);
      await Promise.all([refetchCandidates(), refetchSummary()]);
      if (payload?.success) setTab('LIVE');
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not open the case', {
        variant: 'error',
      });
    } finally {
      setOpening(false);
    }
  };

  /**
   * Pause and resume, one handler.
   *
   * Pausing sends nothing and undoes nothing: the case keeps its status and
   * steps, the sweep skips it, and both buttons go dark. It exists so a case
   * operations has decided to finish by hand stops competing with them --
   * Sensorise allows one active SR per ICCID, and the automatic path firing
   * into a card with a hand-raised SR is how case 52d313cd failed on
   * 2026-09-22. Resume clears the flag; the next sweep carries on.
   */
  const setPaused = async (row: PdCase, paused: boolean, note?: string): Promise<void> => {
    setBusyCase(row.caseId);
    try {
      const res = paused
        ? await pauseCase({ variables: { caseId: row.caseId, note: note || undefined } })
        : await resumeCase({ variables: { caseId: row.caseId } });
      const payload = paused ? res.data?.pausePdCase : res.data?.resumePdCase;
      enqueueSnackbar(payload?.message || (paused ? 'Paused' : 'Resumed'), {
        variant: payload?.success ? 'success' : 'warning',
      });
      await Promise.all([refetch(), refetchSummary()]);
    } catch (err: any) {
      enqueueSnackbar(err?.message || `Could not ${paused ? 'pause' : 'resume'} the case`, {
        variant: 'error',
      });
    } finally {
      setBusyCase(null);
    }
  };

  const confirmPause = async (): Promise<void> => {
    if (!pausing) return;
    const row = pausing;
    const note = pauseNote.trim();
    setPausing(null);
    setPauseNote('');
    await setPaused(row, true, note);
  };

  const openKyc = (row: PdCase): void => {
    setKycForm(EMPTY_KYC);
    setKycAnswer(null);
    setKycFor(row);
  };

  /**
   * Send the KYC and, through it, step 1.
   *
   * Only filled fields go up: the backend writes what it is given onto the
   * vehicle's KYC row and leaves the rest alone, so a blank here never wipes a
   * name or mobile already on file. On success the OTC RSU has gone to
   * Sensorise (the fee is charged then) and the dialog closes. On a refusal the
   * KYC is still saved and the dialog stays open with the server's list of
   * what is missing, so the operator can fill that gap and send again.
   */
  const confirmKyc = async (): Promise<void> => {
    if (!kycFor) return;
    const input = Object.fromEntries(
      Object.entries(kycForm)
        .map(([k, v]) => [k, v.trim()])
        .filter(([, v]) => v),
    ) as PdCaseKycInput;
    setSavingKyc(true);
    try {
      const res = await supplyKyc({ variables: { caseId: kycFor.caseId, input } });
      const payload = res.data?.supplyPdCaseKyc;
      if (payload?.success) {
        enqueueSnackbar(payload.message || 'KYC recorded; step 1 sent', { variant: 'success' });
        setKycFor(null);
      } else {
        setKycAnswer(payload?.message || 'The step could not be sent');
      }
      await Promise.all([refetch(), refetchSummary()]);
    } catch (err: any) {
      setKycAnswer(err?.message || 'Could not record the KYC');
    } finally {
      setSavingKyc(false);
    }
  };

  // POI and POA each need a type and a number to be of any use. The personal
  // fields are optional: most blocked cases already have them on file.
  const kycReady =
    Boolean(kycForm.proofOfIdentity) === Boolean(kycForm.poiIdNumber.trim()) &&
    Boolean(kycForm.proofOfAddress) === Boolean(kycForm.poaIdNumber.trim()) &&
    Object.values(kycForm).some((v) => v.trim());

  /**
   * Close: end an unfinished case for good -- typically one that FAILED and
   * nobody is going to retry, which otherwise sits in "All open" forever.
   * Not a pause: the server refuses to resume, cancel or advance a CLOSED
   * case, and a redelivered payment for it does not open a new one. Nothing
   * is sent to Sensorise. Reviving the same SIM later is a new case.
   */
  const confirmClose = async (): Promise<void> => {
    if (!closing) return;
    const reason = closeReason.trim();
    if (!reason) return;
    const row = closing;
    setClosing(null);
    setCloseReason('');
    setBusyCase(row.caseId);
    try {
      const res = await closeCase({ variables: { caseId: row.caseId, reason } });
      const payload = res.data?.closePdCase;
      enqueueSnackbar(payload?.message || 'Closed', {
        variant: payload?.success ? 'success' : 'warning',
      });
      await Promise.all([refetch(), refetchSummary()]);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not close the case', { variant: 'error' });
    } finally {
      setBusyCase(null);
    }
  };

  /**
   * Hold: open the case already paused, sending nothing and paying no fee.
   *
   * For a SIM the team is reviving by hand. The case then exists, so when the
   * customer's portal payment arrives it attaches to this held case instead
   * of opening a live one that fires the OTC RSU into a card that already has
   * a hand-raised SR open. No confirmation dialog: nothing is spent and it is
   * undone with Cancel on the board.
   */
  const holdCandidate = async (c: PdCandidate): Promise<void> => {
    setBusyCase(c.iccid);
    try {
      const res = await openCase({
        variables: { iccid: c.iccid, hold: true, note: 'Being handled manually' },
      });
      const payload = res.data?.openPdCase;
      enqueueSnackbar(payload?.message || 'Case opened on hold', {
        variant: payload?.success ? 'success' : 'warning',
      });
      await Promise.all([refetchCandidates(), refetchSummary()]);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not put the SIM on hold', { variant: 'error' });
    } finally {
      setBusyCase(null);
    }
  };

  const badges = [
    { label: 'Awaiting RSU', value: summary?.AWAITING_RSU, color: '#E60012' },
    { label: 'Awaiting BPC', value: summary?.AWAITING_BPC, color: '#ed6c02' },
    { label: 'In progress', value: summary?.IN_PROGRESS, color: '#0288d1' },
    { label: 'Completed', value: summary?.COMPLETED, color: '#2e7d32' },
    // Cases a person has stopped and has to remember to come back to.
    { label: 'Paused', value: summary?.PAUSED, color: '#6a1b9a' },
    { label: 'Closed', value: summary?.CLOSED, color: '#455a64' },
    // Terminated SIMs with no case at all: the size of the problem still
    // waiting, and how you notice the recharge portal has stopped opening
    // cases. The matching tab is where a revival is started by hand.
    { label: 'Not started', value: summary?.CANDIDATES, color: '#616161' },
  ];

  /**
   * The single action a case offers right now.
   *
   * Returns null when there is nothing to press, so the cell can show what the
   * case is waiting on instead of a dead button — "IMSI procurement, sent 6h
   * ago" is more use than a greyed-out RSU with no explanation.
   */
  const actionFor = (row: PdCase): 'RSU' | 'BPC' | null => {
    if (row.status === 'AWAITING_RSU' || row.canTriggerRsu) return 'RSU';
    if (row.status === 'AWAITING_BPC' || row.canTriggerBpc) return 'BPC';
    return null;
  };

  // Skip RSU is hidden for now: the only real attempt (case 4cbdb0e3,
  // 2026-11-02) was accepted, but the following BPC was rejected by Sensorise
  // with "Data Rejected: data: RSU Required". Re-enable the button below and
  // this predicate together if operations decides to offer the bypass again.
  //
  const canSkipRsu = (row: PdCase): boolean => {
    if (
      row.pausedAt ||
      ['COMPLETED', 'FAILED', 'CANCELLED', 'CLOSED'].includes(row.status)
    ) {
      return false;
    }
    const rsu = row.steps.find((step) => step.stepKey === 'RSU');
    const sensoriseSaysNotRequired =
      rsu?.status === 'SENT' &&
      String(rsu.remoteStatus || '').trim().toUpperCase() === 'NOT_REQUIRED';
    return Boolean(
      rsu &&
      (['PENDING', 'FAILED'].includes(rsu.status) || sensoriseSaysNotRequired) &&
      row.steps
        .filter((step) => step.seq < rsu.seq)
        .every((step) => ['DONE', 'SKIPPED'].includes(step.status)),
    );
  };

  const waitingOn = (row: PdCase): string => {
    if (row.pausedAt) return `Paused${row.pauseNote ? ` — ${row.pauseNote}` : ''}`;
    if (row.status === 'CLOSED') return row.note ? row.note : 'Closed';
    if (row.status === 'AWAITING_RSU') {
      return getRsuStatusMessage(row.status, row.rsuReadyAt, nowSeconds);
    }
    const step = row.steps.find((s) => !['DONE', 'SKIPPED'].includes(s.status));
    if (!step) return 'Finished';
    return `${step.label} — ${step.status.toLowerCase()}`;
  };

  const rsuReady = (row: PdCase): boolean => isRsuReadyAt(row.rsuReadyAt, nowSeconds);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: '1.35rem', fontWeight: 700, color: '#1F2937', mb: 2,
        }}
      >
        PD Cases
      </Typography>

      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mb: 2, flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex', alignItems: 'center', backgroundColor: '#f0f0f0',
            borderRadius: '24px', padding: '4px', gap: '4px', flexWrap: 'wrap',
          }}
        >
          {TABS.map((t) => (
            <Button
              key={t.key}
              disableRipple
              onClick={() => setTab(t.key)}
              sx={subTabStyle(tab === t.key)}
            >
              {t.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {badges.map((b) => (
            <Box
              key={b.label}
              sx={{
                minWidth: 110, px: 2, py: 1, borderRadius: 2, backgroundColor: '#fff',
                border: '1px solid #e0e0e0', borderTop: `3px solid ${b.color}`,
                boxShadow: '0 2px 6px -4px #b0b0b0', textAlign: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: '1.25rem', fontWeight: 700, color: b.color, lineHeight: 1 }}
              >
                {b.value ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#666', mt: 0.5 }}>
                {b.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Card
        sx={{
          width: '100%', maxWidth: '100vw', mx: 'auto', boxShadow: 2, borderRadius: 3, p: 0,
          display: 'flex', flexDirection: 'column',
          height: { xs: 'auto', md: 'calc(93vh - 190px)' }, bgcolor: '#fff', minHeight: 100,
        }}
      >
        <Box
          sx={{
            position: 'sticky', top: 0, zIndex: 20, background: '#fff',
            borderTopLeftRadius: 8, borderTopRightRadius: 8,
            boxShadow: '0 2px 8px -4px #e0e0e0', px: { xs: 1, sm: 2 }, pt: 2, pb: 2,
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            sx={{
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' }, gap: 2,
            }}
          >
            <Typography sx={{ fontSize: '0.85rem', color: '#666' }}>
              {onCandidates
                ? 'Permanently disconnected SIMs with no revival under way. Starting one sends the OTC RSU immediately, which carries a fee.'
                : 'Permanently disconnected SIMs being revived. Steps 1–2 run automatically; the RSU and the bill plan change are pressed here.'}
            </Typography>

            <TextField
              size="small"
              placeholder="VIN / IMEI / ICCID / customer"
              variant="outlined"
              sx={{ minWidth: 260, background: '#f5f5f5' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Date-only: the tabs above already are the status filter, so a
                second status control would just contradict them. Filters on
                opened_at, not updated_at -- the sweep touches updated_at every
                15 minutes, so a range over it would reshuffle as cases move. */}
            <DateRangeFilter
              label="Opened"
              from={fromDate}
              to={toDate}
              background="#f5f5f5"
              onFromChange={setFromDate}
              onToChange={setToDate}
            />

            {/* Says the page is reading itself, so nobody sits on F5. The
                age is live off the same 1s tick the RSU countdown uses. */}
            <Tooltip
              arrow
              placement="top"
              title={`This board re-reads itself every ${POLL_MS / 1000}s (paused while the tab is hidden). Click to read it now.`}
            >
              <Button
                size="small"
                onClick={() => { void refetchAll(); }}
                startIcon={
                  listLoading
                    ? <CircularProgress size={12} sx={{ color: '#2e7d32' }} />
                    : <RefreshIcon sx={{ fontSize: 16 }} />
                }
                sx={{
                  textTransform: 'none', fontSize: '0.72rem', color: '#666',
                  whiteSpace: 'nowrap', minWidth: 'auto',
                }}
              >
                {listLoading ? 'Updating…' : `Updated ${fmtAgoEpoch(lastLoadedAt)}`}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, py: 2,
            overflow: 'auto', display: 'flex', flexDirection: 'column',
          }}
        >
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none', borderRadius: 2, width: '100%',
                minWidth: 600, background: '#fff',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    {columns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012', color: '#fff', fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.85rem' }, whiteSpace: 'nowrap',
                          padding: { xs: '6px', sm: '8px' }, borderBottom: '1px solid #e0e0e0',
                          borderRight: '2px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {listLoading && !listLength ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : listError ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{ py: 4, color: '#d32f2f' }}
                      >
                        {listError.message}
                      </TableCell>
                    </TableRow>
                  ) : listLength === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{ fontSize: '1rem', color: '#888', py: 4 }}
                      >
                        {onCandidates
                          ? 'No terminated SIMs waiting. Every one of them already has a case.'
                          : 'Nothing here. Revivals are started from the “Not started” tab.'}
                      </TableCell>
                    </TableRow>
                  ) : onCandidates ? (
                    candidates.map((c, index) => (
                      <TableRow key={c.iccid} hover>
                        <TableCell align="center" sx={cellStyle}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {c.customerName || 'N/A'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {c.customerMobile || 'N/A'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {c.vin || 'N/A'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {c.imei || 'N/A'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {c.iccid}
                        </TableCell>

                        {/* Amber, not red: incomplete KYC does not stop the case
                            opening. Step 1 waits BLOCKED -- nothing sent, no
                            fee -- until it is added with Add KYC. */}
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              c.hasKycRecord
                                ? 'KYC complete: owner details and both proofs are on file, so step 1 can go out.'
                                : 'KYC incomplete (usually no ID or address proof). The case can open, but step 1 waits until you add them with Add KYC.'
                            }
                          >
                            <Chip
                              label={c.hasKycRecord ? 'Complete' : 'Needs KYC'}
                              size="small"
                              sx={{
                                height: 20, fontSize: 11, fontWeight: 700, color: '#fff',
                                backgroundColor: c.hasKycRecord ? '#2e7d32' : '#ed6c02',
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        {/* Usually N/A: Sensorise wipes the subscription dates
                            when it terminates a SIM. Blank here is normal. */}
                        <TableCell align="center" sx={cellStyle}>
                          {fmtEpoch(c.subscriptionEndDate)}
                        </TableCell>

                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              mayOpen
                                ? 'Open a revival and send the OTC RSU now. This carries a fee.'
                                : 'You need create access on PD-CASE to start a revival.'
                            }
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!mayOpen}
                                onClick={() => setConfirming(c)}
                                startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                  height: 24, fontSize: 12, textTransform: 'none',
                                  boxShadow: 'none', backgroundColor: '#2e7d32',
                                  '&:hover': { backgroundColor: '#1b5e20', boxShadow: 'none' },
                                }}
                              >
                                Start
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              mayOpen
                                ? 'Open the case on hold: nothing is sent and no fee is paid. Use this for a SIM you are reviving by hand, so a customer payment attaches here instead of firing the OTC RSU.'
                                : 'You need create access on PD-CASE.'
                            }
                          >
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={!mayOpen || busyCase === c.iccid}
                                onClick={() => holdCandidate(c)}
                                startIcon={<PauseIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                  height: 24, fontSize: 12, textTransform: 'none', ml: 1,
                                  color: '#6a1b9a', borderColor: '#6a1b9a',
                                  '&:hover': { borderColor: '#4a148c', backgroundColor: '#f3e5f5' },
                                }}
                              >
                                Hold
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    rows.map((row, index) => {
                      const action = actionFor(row);
                      const busy = busyCase === row.caseId;
                      const { done, total } = progressOf(row.steps);
                      return (
                        <TableRow key={row.caseId} hover>
                          <TableCell align="center" sx={cellStyle}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            {row.customerName || 'N/A'}
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            {row.customerMobile || 'N/A'}
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            {row.vin || 'N/A'}
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            {row.imei || 'N/A'}
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            {row.iccid}
                          </TableCell>

                          {/* One chip per step, in order. The whole state of a
                              revival at a glance, and the tooltip carries the
                              srId for the call to Sensorise when it has stuck. */}
                          <TableCell align="center" sx={cellStyle}>
                            <Box
                              sx={{
                                display: 'flex', gap: 0.5, justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              {row.steps.map((s) => (
                                <Tooltip
                                  key={s.stepId}
                                  arrow
                                  placement="top"
                                  title={
                                    <>
                                      {s.label}: {s.status}
                                      {s.srId ? ` · srId ${s.srId}` : ''}
                                      {s.tokenNo ? ` · ticket ${s.tokenNo}` : ''}
                                      {s.remoteStatus ? ` · ${s.remoteStatus}` : ''}
                                      {s.sentWithoutMkyc ? ' · sent without KYC block' : ''}
                                      {s.error ? ` — ${s.error}` : ''}
                                    </>
                                  }
                                >
                                  <Chip
                                    label={s.seq}
                                    size="small"
                                    sx={{
                                      height: 20, minWidth: 24, fontSize: 11, fontWeight: 700,
                                      color: '#fff',
                                      backgroundColor: stepChipColor(s.status),
                                    }}
                                  />
                                </Tooltip>
                              ))}
                              <Typography
                                sx={{ fontSize: '0.7rem', color: '#666', ml: 0.5 }}
                              >
                                {done}/{total}
                              </Typography>
                            </Box>
                          </TableCell>

                          <Tooltip title={waitingOn(row)} arrow placement="top">
                            <TableCell align="center" sx={cellStyle}>
                              {fmtAgoEpoch(row.updatedAt)}
                            </TableCell>
                          </Tooltip>

                          <TableCell align="center" sx={cellStyle}>
                            {row.pausedAt ? (
                              // Paused: no step button at all -- the server
                              // would refuse it -- just the way back.
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={`Paused ${fmtAgoEpoch(row.pausedAt)}${row.pauseNote ? ` — ${row.pauseNote}` : ''}. Nothing is sent to Sensorise until resumed.`}
                                >
                                  <Chip
                                    label="Paused"
                                    size="small"
                                    icon={<PauseIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                      height: 22, fontSize: 11, fontWeight: 700,
                                      color: '#fff', backgroundColor: '#6a1b9a',
                                      '& .MuiChip-icon': { color: '#fff' },
                                    }}
                                  />
                                </Tooltip>
                                {!['COMPLETED', 'FAILED', 'CANCELLED', 'CLOSED'].includes(row.status) && (
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                      mayTrigger
                                        ? 'Resume the automatic path. The next sweep continues from where it stopped.'
                                        : 'You need update access on PD-CASE.'
                                    }
                                  >
                                    <span>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!mayTrigger || busy}
                                        onClick={() => setPaused(row, false)}
                                        startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                                        sx={{
                                          height: 24, fontSize: 12, textTransform: 'none',
                                          color: '#2e7d32', borderColor: '#2e7d32',
                                          '&:hover': { borderColor: '#1b5e20', backgroundColor: '#e8f5e9' },
                                        }}
                                      >
                                        Resume
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            ) : kycBlockedStep(row) ? (
                              // Blocked on KYC: nothing has gone to Sensorise and
                              // nothing will until the proofs are on file, so the
                              // one useful action is to add them.
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  mayTrigger
                                    ? `${kycBlockedStep(row)?.error || 'Waiting on KYC'}. Add it to send step 1.`
                                    : 'You need update access on PD-CASE.'
                                }
                              >
                                <span>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    disabled={!mayTrigger || busy}
                                    onClick={() => openKyc(row)}
                                    startIcon={<BadgeIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                      height: 24, fontSize: 12, textTransform: 'none',
                                      boxShadow: 'none', backgroundColor: '#ed6c02',
                                      '&:hover': { backgroundColor: '#d65f00', boxShadow: 'none' },
                                    }}
                                  >
                                    Add KYC
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : action ? (
                              <>
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    mayTrigger
                                      ? action === 'RSU'
                                        ? row.status === 'AWAITING_RSU'
                                          ? getRsuStatusMessage(row.status, row.rsuReadyAt, nowSeconds)
                                          : 'Send the RSU. The vehicle must be switched on and in network coverage.'
                                        : 'Buy the 25-month resumption plan and finish the revival.'
                                      : 'You need update access on PD-CASE to send this.'
                                  }
                                >
                                  <span>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disabled={
                                        !mayTrigger ||
                                        busy ||
                                        (action === 'RSU' && row.status === 'AWAITING_RSU' && !rsuReady(row)) ||
                                        (action === 'RSU' && !row.canTriggerRsu) ||
                                        (action === 'BPC' && !row.canTriggerBpc)
                                      }
                                      onClick={() => fire(row, action)}
                                      startIcon={
                                        busy ? (
                                          <CircularProgress size={12} sx={{ color: '#fff' }} />
                                        ) : (
                                          <BoltIcon sx={{ fontSize: 14 }} />
                                        )
                                      }
                                      sx={{
                                        height: 24, fontSize: 12, textTransform: 'none',
                                        boxShadow: 'none',
                                        backgroundColor: action === 'RSU' ? '#E60012' : '#ed6c02',
                                        '&:hover': {
                                          backgroundColor: action === 'RSU' ? '#c1000f' : '#d65f00',
                                          boxShadow: 'none',
                                        },
                                      }}
                                    >
                                      {busy ? 'Sending…' : action}
                                    </Button>
                                  </span>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
                                {waitingOn(row)}
                              </Typography>
                            )}
                            {/* Skip RSU is hidden for now: Sensorise rejected
                                the BPC that followed the one real skip with
                                "RSU Required". Re-enable this block together
                                with canSkipRsu above. */}
                            {canSkipRsu(row) && (
                              <Tooltip
                                arrow
                                placement="top"
                                title="Mark the pending RSU trigger as skipped. This will not send the BPC."
                              >
                                <span>
                                  <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    disabled={!mayTrigger || busy}
                                    onClick={() => setSkippingRsuFor(row)}
                                    sx={{
                                      height: 24, fontSize: 11, textTransform: 'none',
                                      ml: 0.75, px: 0.75, whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Skip RSU
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            {/* The pause, on every live row that is not
                                already paused. Small and to the right so it
                                never reads as the step to press. */}
                            {!row.pausedAt &&
                              !['COMPLETED', 'FAILED', 'CANCELLED', 'CLOSED'].includes(row.status) && (
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    mayTrigger
                                      ? 'Pause: stop the automatic path so this revival can be finished by hand. Nothing is undone; resume whenever.'
                                      : 'You need update access on PD-CASE.'
                                  }
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      disabled={!mayTrigger || busy}
                                      onClick={() => { setPauseNote(''); setPausing(row); }}
                                      sx={{
                                        minWidth: 0, height: 24, ml: 1, px: 0.5,
                                        color: '#6a1b9a', textTransform: 'none',
                                      }}
                                    >
                                      <PauseIcon sx={{ fontSize: 16 }} />
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            {/* Close, on every unfinished row -- FAILED and
                                paused included, since those are the ones that
                                otherwise never leave the queue. */}
                            {!['COMPLETED', 'CANCELLED', 'CLOSED'].includes(row.status) && (
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  mayTrigger
                                    ? 'Close this case for good. It leaves the open queue and cannot be reopened. Nothing is sent to Sensorise.'
                                    : 'You need update access on PD-CASE.'
                                }
                              >
                                <span>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={!mayTrigger || busy}
                                    onClick={() => { setCloseReason(''); setClosing(row); }}
                                    startIcon={<DoDisturbOnIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                      height: 24, fontSize: 12, textTransform: 'none', ml: 1,
                                      color: '#455a64', borderColor: '#90a4ae',
                                      '&:hover': { borderColor: '#455a64', backgroundColor: '#eceff1' },
                                    }}
                                  >
                                    Close
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'sticky', bottom: 0, zIndex: 20, background: '#fff',
            borderTop: '1px solid #e0e0e0', pt: 2, mt: 'auto', overflowX: 'auto',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Rows per page:"
          />
        </Box>
      </Card>

      {/* Add KYC. Saving sends step 1 straight away when the record is then
          complete, and that is the OTC RSU fee -- so the dialog says so, and
          shows what the server is waiting on rather than guessing. */}
      <Dialog
        open={Boolean(kycFor)}
        onClose={() => (savingKyc ? null : setKycFor(null))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Add KYC for {kycFor?.customerName || 'this vehicle'}
          {kycFor?.vin ? ` (${kycFor.vin})` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem', mb: 1 }}>
            Sensorise refuses a revival without the owner&apos;s ID and address
            proof. Enter the documents the customer or dealer gave you; they must
            match the KYC Sensorise already holds for this SIM. Once the record
            is complete, step 1 (OTC RSU) is sent immediately and its fee is
            charged.
          </DialogContentText>
          {kycFor && (
            <Alert severity="info" sx={{ fontSize: '0.78rem', mb: 2 }}>
              {kycBlockedStep(kycFor)?.error || 'Step 1 is waiting on KYC.'}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              select
              size="small"
              label="ID proof type"
              value={kycForm.proofOfIdentity}
              onChange={(e) => setKycForm({ ...kycForm, proofOfIdentity: e.target.value })}
            >
              <MenuItem value=""><em>Keep what is on file</em></MenuItem>
              {POI_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="ID proof number"
              value={kycForm.poiIdNumber}
              onChange={(e) => setKycForm({ ...kycForm, poiIdNumber: e.target.value })}
            />
            <TextField
              select
              size="small"
              label="Address proof type"
              value={kycForm.proofOfAddress}
              onChange={(e) => setKycForm({ ...kycForm, proofOfAddress: e.target.value })}
            >
              <MenuItem value=""><em>Keep what is on file</em></MenuItem>
              {POA_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Address proof number"
              value={kycForm.poaIdNumber}
              onChange={(e) => setKycForm({ ...kycForm, poaIdNumber: e.target.value })}
            />
          </Box>

          <Typography sx={{ fontSize: '0.78rem', color: '#666', mt: 2, mb: 1 }}>
            Only if missing or wrong on file. Left blank, the stored value is kept.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              size="small"
              label={`Owner name${kycFor?.customerName ? ` (on file: ${kycFor.customerName})` : ''}`}
              value={kycForm.cusName}
              onChange={(e) => setKycForm({ ...kycForm, cusName: e.target.value })}
            />
            <TextField
              size="small"
              label={`Owner mobile${kycFor?.customerMobile ? ` (on file: ${kycFor.customerMobile})` : ''}`}
              value={kycForm.cusContNo}
              onChange={(e) => setKycForm({ ...kycForm, cusContNo: e.target.value })}
            />
            <TextField
              size="small"
              label="Owner address"
              value={kycForm.cusAdrs}
              onChange={(e) => setKycForm({ ...kycForm, cusAdrs: e.target.value })}
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
          </Box>

          {kycAnswer && (
            <Alert severity="warning" sx={{ fontSize: '0.78rem', mt: 2 }}>
              {kycAnswer}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setKycFor(null)}
            disabled={savingKyc}
            sx={{ textTransform: 'none' }}
          >
            {kycAnswer ? 'Close' : 'Cancel'}
          </Button>
          <Tooltip
            arrow
            placement="top"
            title={kycReady ? '' : 'Give each proof both a type and a number.'}
          >
            <span>
              <Button
                variant="contained"
                onClick={confirmKyc}
                disabled={savingKyc || !kycReady}
                startIcon={
                  savingKyc
                    ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                    : <BadgeIcon sx={{ fontSize: 14 }} />
                }
                sx={{
                  textTransform: 'none', boxShadow: 'none', backgroundColor: '#ed6c02',
                  '&:hover': { backgroundColor: '#d65f00', boxShadow: 'none' },
                }}
              >
                {savingKyc ? 'Sending…' : 'Save and send step 1'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Pause: one optional note. Cheap and reversible, so no warning. */}
      <Dialog
        open={Boolean(pausing)}
        onClose={() => setPausing(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Pause this revival?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem', mb: 2 }}>
            Nothing more goes to Sensorise for{' '}
            <b>{pausing?.customerName || 'this vehicle'}</b>
            {pausing?.vin ? ` (${pausing.vin})` : ''} until you resume it. Steps
            already sent stay as they are. Use this when the team is finishing
            the revival by hand.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Note (optional)"
            placeholder="e.g. being handled manually by Ravi"
            value={pauseNote}
            onChange={(e) => setPauseNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPausing(null)} sx={{ textTransform: 'none' }}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={confirmPause}
            startIcon={<PauseIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none', boxShadow: 'none', backgroundColor: '#6a1b9a',
              '&:hover': { backgroundColor: '#4a148c', boxShadow: 'none' },
            }}
          >
            Pause
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close: permanent, so a required reason and a plain warning. */}
      <Dialog
        open={Boolean(closing)}
        onClose={() => setClosing(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Close this case?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem', mb: 2 }}>
            The case for <b>{closing?.customerName || 'this vehicle'}</b>
            {closing?.vin ? ` (${closing.vin})` : ''} is marked closed and taken
            off the open queue. Nothing is sent to Sensorise.
          </DialogContentText>
          <Alert severity="warning" sx={{ fontSize: '0.78rem', mb: 2 }}>
            This cannot be undone — unlike pause, a closed case is never resumed.
            To revive this SIM later, start a new case.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            required
            size="small"
            label="Reason"
            placeholder="e.g. OTC RSU rejected, customer refunded"
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClosing(null)} sx={{ textTransform: 'none' }}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={confirmClose}
            disabled={!closeReason.trim()}
            startIcon={<DoDisturbOnIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none', boxShadow: 'none', backgroundColor: '#455a64',
              '&:hover': { backgroundColor: '#263238', boxShadow: 'none' },
            }}
          >
            Close case
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(skippingRsuFor)}
        onClose={() =>
          busyCase === skippingRsuFor?.caseId ? null : setSkippingRsuFor(null)
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Skip RSU?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem', mb: 2 }}>
            This will only mark the pending RSU trigger as skipped for{' '}
            <b>{skippingRsuFor?.customerName || 'this vehicle'}</b>
            {skippingRsuFor?.vin ? ` (${skippingRsuFor.vin})` : ''}. It will
            not send the BPC or any plan request. You can trigger BPC separately
            after the RSU step is skipped.
          </DialogContentText>
          <Alert severity="warning" sx={{ fontSize: '0.78rem' }}>
            Continue only when operations has confirmed the RSU step can be
            bypassed. OTC RSU and IMSI must already be complete.
          </Alert>
          <Typography sx={{ fontSize: '0.78rem', color: '#666', mt: 1 }}>
            ICCID {skippingRsuFor?.iccid}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSkippingRsuFor(null)}
            disabled={busyCase === skippingRsuFor?.caseId}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmSkipRsu}
            disabled={busyCase === skippingRsuFor?.caseId}
            startIcon={
              busyCase === skippingRsuFor?.caseId ? (
                <CircularProgress size={12} sx={{ color: '#fff' }} />
              ) : (
                <BoltIcon sx={{ fontSize: 14 }} />
              )
            }
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {busyCase === skippingRsuFor?.caseId ? 'Skipping…' : 'Skip RSU'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation, because confirming SPENDS MONEY.
          openPdCase inserts the case and immediately fires step 1, a Commercial
          Activation carrying the OTC- RSU fee. There is no undo -- cancelling
          the case afterwards does not unspend it -- so the dialog names the SIM
          being committed and says plainly what the click costs. */}
      <Dialog
        open={Boolean(confirming)}
        onClose={() => (opening ? null : setConfirming(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Start a revival?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem', mb: 2 }}>
            This opens a case for{' '}
            <b>{confirming?.customerName || 'this vehicle'}</b>
            {confirming?.vin ? ` (${confirming.vin})` : ''} and sends the OTC RSU
            to Sensorise straight away. The fee is charged on that request and
            cannot be taken back.
          </DialogContentText>

          <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>
            ICCID {confirming?.iccid}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#666', mb: 1 }}>
            The IMSI step then runs on its own for about a day. You will be asked
            to press RSU once it lands.
          </Typography>

          {confirming && !confirming.hasKycRecord && (
            <Alert severity="warning" sx={{ fontSize: '0.78rem', mt: 1 }}>
              This vehicle&apos;s KYC is incomplete. The case will open, but step 1
              waits (nothing sent, no fee) until you add the owner&apos;s ID and
              address proofs with <b>Add KYC</b> on the case.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirming(null)}
            disabled={opening}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmOpen}
            disabled={opening}
            startIcon={
              opening ? (
                <CircularProgress size={12} sx={{ color: '#fff' }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              textTransform: 'none', boxShadow: 'none', backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20', boxShadow: 'none' },
            }}
          >
            {opening ? 'Starting…' : 'Start revival'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PdCases;
